// src/lib/spells.js — as-of readers for the three DATED person tables.
//
// Owner ruling 2026-09-20: "a person's contribution depends on their role, the role is defined
// by dates — and not only commission, anything happening is stamped by date and is active [for
// a period]". Office membership (scheduler 0001), manager coverage (0001/0007) and commission
// rates (0025) are all SPELLS: a start, an end (NULL = still open) and a value. None of them is
// a property of the person's current row, and reading the current row answers a question about
// today when the question was about the close date.
//
// ⚠️ WHY THIS IS ITS OWN MODULE AND NOT PART OF statsModel.js. `statsModel` imports from
// `performance` (agentDealIncome, managerDealIncome, viewingCloseDate), so `performance`
// importing these back would close an ESM cycle. A cycle here is not theoretical: it resolves
// as a TDZ ReferenceError at module-eval time, which `vite build` passes and which has taken
// the whole Desk down once already (2026-08-28, `getKindFeatureCapability is not defined`).
// This module imports NOTHING, so it can never be the far side of a cycle.
//
// Intervals are [start, end) on DAY precision, matching how close dates are stored. A row that
// opens and closes the same day covers that day.

// Which office an agent belonged to on `date`. null when unknown — and unknown must never
// silently become "the office they are in now", which is the whole defect being fixed.
export function officeAt(agentId, date, memberships = []) {
  const r = memberships.find(m => m.agent_id === agentId && m.joined_at <= date
                              && (!m.left_at || m.left_at > date))
  return r ? r.office_id : null
}

// Which offices a manager covered on `date`. Empty = they were not a manager then, so no team
// work is theirs — exactly the period boundary the owner asked for.
export function officesCoveredAt(managerId, date, coverage = []) {
  return coverage.filter(c => c.agent_id === managerId && c.joined_at <= date
                          && (!c.left_at || c.left_at > date)).map(c => c.office_id)
}

// What percentage this person was on `date` — `kind` is 'agent' (their own cut of a deal they
// worked) or 'manager' (the office override on their team's deals). null when no spell covers
// that day, and null must NOT be read as zero: it means we do not know, which is a different
// answer from "they were owed nothing".
//
// ⚠️ THIS IS THE READER THE LEDGER WAS MISSING. Until 2026-09-20 every consumer took the rate
// straight off the roster row, so a raise today silently re-priced every historical deal, and a
// rate that had been CLEARED (a manager who left the role) read as zero for periods they were
// genuinely owed. Measured that day: Tako Kharkhelauri and Gvantsa Karpovi both covered
// Business Center Delisi through August with commission_manager now NULL, so the callers'
// `!(rate > 0)` test skipped them BEFORE coverage was consulted — their overrides were not paid
// at a wrong rate, they were not paid at all.
// ⚠️ The spells cannot resurrect a value erased before 0025 (the backfill seeded from current
// values, so a NULL column produced no row at all). Those two still return null here. What this
// fixes is every change from 0025 forward.
export function rateAt(agentId, kind, date, rates = []) {
  const r = rates.find(x => x.agent_id === agentId && x.kind === kind
                         && x.started_at <= date && (!x.ended_at || x.ended_at > date))
  return r ? Number(r.pct) : null
}

// The office's working day as it stood on `date` (scheduler 0026). Returns the spell row, or
// null when none covers that day — and null means "we have no record", which the caller must
// turn into the office's current row (the pre-0026 answer), never into an invented day.
//
// ⚠️ ALL FOUR TIMES TRAVEL TOGETHER. They describe one fact — what this office's working day is
// — so the spell is the unit. Reading weekday hours from one spell and weekend hours from
// another would reconstruct a week that never existed.
// ⚠️ NULL weekend hours are a REAL value ("this office has no weekend day", two of the four
// offices), not a missing one. A caller must not treat a spell with NULL weekend times as an
// absent spell and fall through to the current row.
export function officeHoursAt(officeId, date, hours = []) {
  return hours.find(h => h.office_id === officeId && h.started_at <= date
                      && (!h.ended_at || h.ended_at > date)) || null
}

// ─── The six-fact resolver (owner ruling 2026-09-20) ─────────────────────────────────────────
// "The system must always know what happened, when, under which manager, which agents were
// involved, who filed it, and under which office."
//
// Four of those six are recorded on the event itself: WHAT (verb), WHEN (occurred_at), WHO FILED
// IT (actor_party_id) and WHICH AGENTS were involved (the subject's own links — deal.agent_links,
// cooperation_participant, meeting_attendee). The remaining two are NOT stored anywhere and
// deliberately should not be: OFFICE and MANAGER are functions of the actor and the date, and
// 3,337,162 core events copying them would be 3,337,162 chances to disagree with the spell tables
// they were copied from. This resolves them instead.
//
// ⚠️ THE ACTOR IS THE IRREDUCIBLE FACT. Office and manager both derive FROM it, so an event with
// no actor can never be given any of the three. 213,903 core events are in that state (all before
// 2026-07-27, when B160 moved attribution into the transport); they are unrecoverable, and this
// function honestly returns nulls for them rather than guessing from anything else on the row.
//
// ⚠️ "WHO FILED IT" AND "WHO WAS INVOLVED" ARE DIFFERENT QUESTIONS and diverge constantly here —
// a manager amending another agent's terms, an admin closing someone else's deal, core's 0207
// rule that the cooperation actor is the session while p_participant_party_id names whose terms
// row is edited. This answers the FILER's context. Feed it a participant's id and you get that
// participant's context, which is a different (also valid) question — just never the same one.
export function contextAt(agentId, date, history = {}) {
  const { memberships = [], coverage = [] } = history
  if (!agentId || !date) return { office_id: null, manager_ids: [], self_managed: false, known: false }
  const office_id = officeAt(agentId, date, memberships)
  if (!office_id) return { office_id: null, manager_ids: [], self_managed: false, known: false }
  // Everyone covering that office THAT DAY. More than one is a real state, not corrupt data:
  // Business Center Delisi genuinely had two concurrent managers 2026-08-06 → 08-10.
  const covering = [...new Set(
    coverage.filter(c => c.office_id === office_id && c.joined_at <= date
                      && (!c.left_at || c.left_at > date))
            .map(c => c.agent_id))]
  return {
    office_id,
    // A person is not their own manager. Reported separately rather than dropped, because
    // "this agent answered to nobody that day" and "this agent WAS the manager" are different
    // answers and a caller attributing work upward needs to tell them apart.
    manager_ids: covering.filter(id => id !== agentId),
    self_managed: covering.includes(agentId),
    known: true,
  }
}

// Same question asked from CORE, where the actor is a party id rather than a scheduler agent id.
// The bridge is party.scheduler_agent_id (one core party per scheduler agent, reserved since
// core 0001) — pass it as a Map or plain object; this module stays pure and fetches nothing.
// ⚠️ An unbridged party — an outside co-broke agent, a client, a scraped contact — resolves to
// known:false. That is correct and must not be softened into "no office": they have no office in
// OUR structure, which is a different statement from our not knowing theirs.
export function contextAtForParty(partyId, date, history = {}, partyToAgent = {}) {
  const get = partyToAgent instanceof Map ? k => partyToAgent.get(k) : k => partyToAgent[k]
  const agentId = partyId ? get(partyId) : null
  if (!agentId) return { office_id: null, manager_ids: [], self_managed: false, known: false }
  return contextAt(agentId, date, history)
}
